import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Check, Plus, Minus, Trash2, ShoppingBag, Star, ShoppingCart } from 'lucide-react';
import { apiFetch } from '../../pages/marketplace/api';

const styles = `
  .atc-overlay {
    position: fixed; inset: 0; z-index: 300;
    background: rgba(8,10,22,0.72);
    backdrop-filter: blur(6px);
    overflow-y: auto;
    font-family: 'Nunito Sans', sans-serif;
    animation: atc-fade 0.18s ease;
  }
  @keyframes atc-fade { from { opacity: 0 } to { opacity: 1 } }
  .atc-sheet {
    max-width: 1080px;
    margin: 40px auto;
    padding: 0 16px 60px;
    display: grid;
    grid-template-columns: 1fr 300px;
    grid-template-areas: "confirm rail" "suggest rail";
    gap: 20px;
    align-items: start;
  }
  .atc-area-confirm { grid-area: confirm; }
  .atc-area-suggest { grid-area: suggest; }
  .atc-rail { grid-area: rail; }
  /* On a phone: what you added, then the cart and its buttons, then more to browse. */
  @media (max-width: 900px) {
    .atc-sheet {
      grid-template-columns: 1fr;
      grid-template-areas: "confirm" "rail" "suggest";
      margin: 12px auto;
    }
    .atc-rail { position: static !important; }
  }
  .atc-card {
    background: #faf8f4;
    border-radius: 16px;
    border: 1px solid #e8e0d4;
    padding: 18px;
  }
  .atc-close {
    position: sticky; top: 0;
    margin-left: auto;
    display: flex; align-items: center; gap: 6px;
    background: rgba(255,255,255,0.10);
    border: 1px solid rgba(255,255,255,0.18);
    color: #f5f0e8;
    border-radius: 999px;
    padding: 8px 14px;
    font-size: 13px;
    cursor: pointer;
  }
  .atc-close:hover { background: rgba(255,255,255,0.2); }
  .atc-confirm { display: flex; align-items: center; gap: 14px; }
  .atc-confirm img, .atc-thumb {
    width: 64px; height: 64px; border-radius: 10px; object-fit: cover;
    background: #efe9df; display: flex; align-items: center; justify-content: center;
    color: #b6a893; flex-shrink: 0;
  }
  .atc-added { font-size: 16px; font-weight: 700; color: #1f6b4d; display: flex; align-items: center; gap: 7px; }
  .atc-title { font-size: 14px; color: #4a3f33; margin: 3px 0 0; }
  .atc-rail { position: sticky; top: 40px; }
  .atc-rail-sub { font-size: 12px; color: #8a7a6a; }
  .atc-rail-total { font-size: 20px; font-weight: 700; color: #1a1a1a; }
  .atc-btn {
    width: 100%; border-radius: 999px; padding: 11px 16px; font-size: 14px; font-weight: 600;
    cursor: pointer; border: none; margin-top: 10px; font-family: inherit;
  }
  .atc-btn.primary { background: #1f6b4d; color: #fff; }
  .atc-btn.primary:hover { background: #185640; }
  .atc-btn.ghost { background: #fff; color: #1f6b4d; border: 1px solid #1f6b4d; }
  .atc-line { display: flex; gap: 10px; padding: 11px 0; border-bottom: 1px solid #ece5d9; }
  .atc-line:last-child { border-bottom: none; }
  .atc-line img, .atc-line .atc-thumb { width: 48px; height: 48px; border-radius: 8px; }
  .atc-line-name { font-size: 12.5px; color: #3a3128; margin: 0 0 4px; line-height: 1.35; }
  .atc-line-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
  .atc-qty { display: flex; align-items: center; gap: 6px; }
  .atc-qty button {
    width: 22px; height: 22px; border-radius: 50%; border: 1px solid #d6cab8;
    background: #fff; color: #4a3f33; cursor: pointer; display: flex;
    align-items: center; justify-content: center;
  }
  .atc-qty button:disabled { opacity: 0.4; cursor: not-allowed; }
  .atc-qty-val { font-size: 12px; font-weight: 700; min-width: 14px; text-align: center; color: #3a3128; }
  .atc-price { font-size: 12.5px; font-weight: 700; color: #1a1a1a; }
  .atc-trash { background: none; border: none; color: #a08c78; cursor: pointer; padding: 2px; }
  .atc-trash:hover { color: #c0392b; }
  .atc-heading { font-family: 'Fraunces', serif; font-size: 20px; color: #2a231c; margin: 0 0 14px; }
  .atc-suggest { display: flex; gap: 14px; padding: 12px 0; border-bottom: 1px solid #ece5d9; }
  .atc-suggest:last-child { border-bottom: none; }
  .atc-suggest img, .atc-suggest .atc-thumb { width: 92px; height: 92px; border-radius: 10px; }
  .atc-suggest-name {
    font-size: 13.5px; color: #1b4f8a; margin: 0 0 4px; cursor: pointer; line-height: 1.35;
  }
  .atc-suggest-name:hover { text-decoration: underline; }
  .atc-stars { display: flex; align-items: center; gap: 3px; color: #c9a84c; font-size: 11px; }
  .atc-stars span { color: #8a7a6a; }
  .atc-suggest-price { font-size: 14px; font-weight: 700; color: #1a1a1a; margin: 5px 0 8px; }
  .atc-add {
    display: inline-flex; align-items: center; gap: 6px;
    border-radius: 999px; padding: 7px 15px; font-size: 12.5px; font-weight: 600;
    background: #f4c542; border: 1px solid #d9ab26; color: #2a231c; cursor: pointer;
  }
  .atc-add:hover { background: #efb92c; }
  .atc-add.done { background: #e7f0e9; border-color: #b9d7c2; color: #1f6b4d; }
`;

const Thumb = ({ src, alt }) => (
  src ? <img src={src} alt={alt} /> : <div className="atc-thumb"><ShoppingBag size={18} /></div>
);

/**
 * "Added to cart" — the overlay that replaced a 2.5-second label.
 *
 * Confirms what went in, gives the whole cart down the right with a quantity stepper and a
 * delete on every line, and keeps the shop going underneath with more from the same category.
 * Closing with the X drops you back exactly where you were, mid-browse.
 */
export function AddedToCartOverlay({ product, variantName, onClose }) {
  const navigate = useNavigate();
  const [cart, setCart] = useState({ items: [] });
  const [suggestions, setSuggestions] = useState([]);

  const loadCart = useCallback(() => {
    apiFetch('/cart')
      .then((c) => { setCart(c); window.dispatchEvent(new Event('cart-updated')); })
      .catch(() => {});
  }, []);

  // onClose is an inline arrow in the parent, so keeping it in the effect's deps re-ran the
  // whole fetch on every parent render — eight cart calls and four related calls per open.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    loadCart();
    if (product?.id) {
      apiFetch(`/marketplace/products/${product.id}/related?limit=6`)
        .then((d) => setSuggestions(d.products || []))
        .catch(() => setSuggestions([]));
    }
    const onKey = (e) => { if (e.key === 'Escape') closeRef.current(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [product?.id, loadCart]);

  const setQty = async (item, qty) => {
    const variant = item.variant_name ? `?variant_name=${encodeURIComponent(item.variant_name)}` : '';
    try {
      if (qty <= 0) await apiFetch(`/cart/items/${item.product_id}${variant}`, { method: 'DELETE' });
      else await apiFetch(`/cart/items/${item.product_id}`, {
        method: 'PATCH',
        body: JSON.stringify({ qty, variant_name: item.variant_name || null }),
      });
      loadCart();
    } catch { /* the cart reload will show the truth */ }
  };

  const addSuggestion = async (p) => {
    // Sold in types — send them to pick, the same as the cards do
    if ((p.variants?.length || 0) > 0) {
      onClose();
      navigate(`/dashboard/marketplace/product/${p.id}`);
      return;
    }
    try {
      await apiFetch('/cart/items', { method: 'POST', body: JSON.stringify({ product_id: p.id, qty: 1 }) });
      loadCart();
    } catch { /* ignore */ }
  };

  const items = cart.items || [];
  const count = items.reduce((s, i) => s + (i.qty || 1), 0);
  const subtotal = items.reduce((s, i) => s + (i.price_snapshot || 0) * (i.qty || 1), 0);
  const inCart = (id) => items.find((i) => i.product_id === id);
  // With two types of the same product in the basket, matching on the id alone confirmed the
  // wrong one — and showed its picture (testing agent, iteration 199).
  const justAdded = items.find((i) => i.product_id === product?.id
    && (!variantName || i.variant_name === variantName));
  const addedImage = justAdded?.image_url || product?.images?.[0];

  return (
    <div className="atc-overlay" data-testid="added-to-cart-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <style>{styles}</style>
      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '14px 16px 0', display: 'flex' }}>
        <button className="atc-close" onClick={onClose} data-testid="atc-close">
          <X size={14} /> Keep shopping
        </button>
      </div>

      <div className="atc-sheet">
        <div className="atc-area-confirm">
          <div className="atc-card atc-confirm" data-testid="atc-confirmation">
            <Thumb src={addedImage} alt={product?.title} />
            <div>
              <p className="atc-added"><Check size={17} /> Added to cart</p>
              <p className="atc-title">{justAdded?.title || product?.title}</p>
              {justAdded?.variant_name && (
                <p style={{ margin: '2px 0 0', fontSize: 12, color: '#8a7a6a' }}
                  data-testid="atc-confirmation-variant">{justAdded.variant_name}</p>
              )}
            </div>
          </div>
        </div>

        <div className="atc-area-suggest">
          {suggestions.length > 0 && (
            <div className="atc-card" data-testid="atc-suggestions">
              <h3 className="atc-heading">More like this</h3>
              {suggestions.map((p) => {
                const line = inCart(p.id);
                return (
                  <div key={p.id} className="atc-suggest" data-testid={`atc-suggestion-${p.id}`}>
                    <Thumb src={p.images?.[0]} alt={p.title} />
                    <div style={{ minWidth: 0 }}>
                      <p className="atc-suggest-name"
                        onClick={() => { onClose(); navigate(`/dashboard/marketplace/product/${p.id}`); }}>
                        {p.title}
                      </p>
                      {p.rating_count > 0 && (
                        <div className="atc-stars">
                          {[1, 2, 3, 4, 5].map((n) => (
                            <Star key={n} size={11} fill={n <= Math.round(p.rating_avg) ? 'currentColor' : 'none'} />
                          ))}
                          <span>{Number(p.rating_avg).toFixed(1)} ({p.rating_count})</span>
                        </div>
                      )}
                      <p className="atc-suggest-price">${Number(p.price).toFixed(2)}</p>
                      {line ? (
                        <span className="atc-add done" data-testid={`atc-added-${p.id}`}>
                          <Check size={13} /> {line.qty} in cart
                        </span>
                      ) : (
                        <button className="atc-add" onClick={() => addSuggestion(p)}
                          data-testid={`atc-add-${p.id}`}>
                          <ShoppingCart size={13} />
                          {(p.variants?.length || 0) > 0 ? 'Choose type' : 'Add to cart'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Cart rail — his first Amazon screenshot: everything in the basket, editable in place */}
        <div className="atc-card atc-rail" data-testid="atc-cart-rail">
          <p className="atc-rail-sub">Cart subtotal ({count} item{count !== 1 ? 's' : ''})</p>
          <p className="atc-rail-total" data-testid="atc-subtotal">${subtotal.toFixed(2)}</p>
          <button className="atc-btn primary"
            onClick={() => navigate('/dashboard/marketplace/cart?checkout=1')}
            data-testid="atc-checkout">
            Proceed to checkout
          </button>
          <button className="atc-btn ghost" onClick={() => navigate('/dashboard/marketplace/cart')}
            data-testid="atc-go-to-cart">
            Go to cart
          </button>

          <div style={{ marginTop: 14 }}>
            {items.map((item) => {
              const single = ['digital', 'session', 'course'].includes(item.type);
              const vk = item.variant_name
                ? `-${item.variant_name.toLowerCase().replace(/\s+/g, '-')}` : '';
              return (
                <div key={item.product_id + (item.variant_name || '')} className="atc-line"
                  data-testid={`atc-line-${item.product_id}${vk}`}>
                  <Thumb src={item.image_url} alt={item.title} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p className="atc-line-name">{item.title}</p>
                    {item.variant_name && (
                      <p style={{ margin: '1px 0 0', fontSize: 11.5, color: '#8a7a6a' }}
                        data-testid={`atc-line-variant-${item.product_id}${vk}`}>
                        {item.variant_name}
                      </p>
                    )}
                    <div className="atc-line-row">
                      {single ? (
                        <span style={{ fontSize: 11, color: '#8a7a6a' }}>Qty 1</span>
                      ) : (
                        <div className="atc-qty">
                          <button onClick={() => setQty(item, item.qty - 1)}
                            disabled={item.qty <= 1}
                            data-testid={`atc-minus-${item.product_id}${vk}`} aria-label="One fewer">
                            <Minus size={11} />
                          </button>
                          <span className="atc-qty-val">{item.qty}</span>
                          <button onClick={() => setQty(item, item.qty + 1)}
                            data-testid={`atc-plus-${item.product_id}${vk}`} aria-label="One more">
                            <Plus size={11} />
                          </button>
                        </div>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className="atc-price">
                          ${((item.price_snapshot || 0) * (item.qty || 1)).toFixed(2)}
                        </span>
                        <button className="atc-trash" onClick={() => setQty(item, 0)}
                          data-testid={`atc-remove-${item.product_id}${vk}`} aria-label="Remove">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddedToCartOverlay;
